import React, { useState } from 'react';
import {
  Check, Code, LayoutTemplate, Palette, RotateCcw, Sparkles, Type,
} from 'lucide-react';
import {
  AppBadge, AppButton, AppCard, AppCardContent, AppCardDescription, AppCardHeader, AppCardTitle,
  Checkbox, ColorInput, ConfirmModal, FieldError, FormField, HelpText, Input, NativeSelect,
  PanelButton, Radio, Tabs, TabsContent, TabsList, TabsTrigger, Textarea,
} from '../ui';
import ImageField from './ImageField';
import {
  BRANDING_MODES, FONT_PRESET_OPTIONS, HERO_TITLE_SCALES, LAYOUT_PRESETS,
} from './cmsSiteModel';

// ─── DesignTab — layout, colours, fonts and custom code ──────────────────
//
// Switching layout rebuilds every page, so it asks first and applies at once;
// everything else here joins the draft and is saved from the save bar.
// Settings that only one layout reads are shown only for that layout.

// The default hero gradient vc_cms paints when none is set. The website does
// not load this app's palette, so it is a literal, not a token.
const DEFAULT_HERO_GRADIENT = ['#0f172a', '#1a1033', '#2d1b69', '#7c3aed'];
const GRADIENT_KEYS = ['heroGradient1', 'heroGradient2', 'heroGradient3', 'heroGradient4'];
const GRADIENT_LABELS = ['Start', 'Second', 'Third', 'End'];

function LayoutCard({ layoutPreset, busy, onChangeLayout }) {
  const [pending, setPending] = useState(null);
  const target = LAYOUT_PRESETS.find((p) => p.id === pending);
  return (
    <AppCard>
      <AppCardHeader>
        <AppCardTitle><LayoutTemplate size={16} /> Layout</AppCardTitle>
        <AppCardDescription>
          The structure of every page. Your content is kept for each layout, so you can switch and come back.
        </AppCardDescription>
      </AppCardHeader>
      <AppCardContent>
        <div className="tw-grid tw-grid-cols-1 tw-gap-3 min-[992px]:tw-grid-cols-3">
          {LAYOUT_PRESETS.map((p) => {
            const active = p.id === layoutPreset;
            return (
              <PanelButton
                key={p.id}
                selected={active}
                disabled={busy}
                onClick={() => { if (!active) setPending(p.id); }}
                className="tw-items-start"
              >
                <span className="tw-flex tw-min-w-0 tw-flex-col tw-gap-1">
                  <span className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                    <span className="tw-text-[0.9375rem] tw-font-semibold tw-text-content">{p.label}</span>
                    {active && <AppBadge size="sm" variant="default"><Check size={12} aria-hidden="true" /> In use</AppBadge>}
                  </span>
                  <span className="tw-text-xs tw-font-medium tw-text-content-muted">{p.tagline}</span>
                  <span className="tw-text-sm tw-leading-snug tw-text-content-muted">{p.description}</span>
                </span>
              </PanelButton>
            );
          })}
        </div>
      </AppCardContent>
      <ConfirmModal
        show={!!target}
        title={`Switch to ${target?.label}?`}
        message={`Every page of your website will take the ${target?.label} layout within a minute. Content written for your current layout is kept and returns if you switch back.`}
        confirmText={`Switch to ${target?.label}`}
        onConfirm={() => { onChangeLayout(pending); setPending(null); }}
        onHide={() => setPending(null)}
      />
    </AppCard>
  );
}

function CustomCodeCard({ customCode, setCode }) {
  const [open, setOpen] = useState(false);
  const inUse = ['customCss', 'customHeadScripts', 'customBodyScripts'].some((k) => customCode[k].trim());
  return (
    <AppCard>
      <AppCardHeader>
        <AppCardTitle><Code size={16} /> Advanced: custom code</AppCardTitle>
        <AppCardDescription>
          Styles and scripts added to every page — analytics tags, a chat widget. Code here runs on your website as written.
        </AppCardDescription>
      </AppCardHeader>
      <AppCardContent className="tw-flex tw-flex-col tw-gap-3">
        {!open ? (
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
            <AppButton size="sm" variant="outline" onClick={() => setOpen(true)}>Show custom code</AppButton>
            {inUse && <AppBadge size="sm" variant="secondary">In use</AppBadge>}
          </div>
        ) : (
          <Tabs defaultValue="css">
            <TabsList>
              <TabsTrigger value="css">CSS</TabsTrigger>
              <TabsTrigger value="head">Head scripts</TabsTrigger>
              <TabsTrigger value="body">Body scripts</TabsTrigger>
            </TabsList>
            {[
              ['css', 'customCss', 'Styles applied to every page.', '.hero-title {\n  letter-spacing: -0.02em;\n}'],
              ['head', 'customHeadScripts', 'Added before </head> — analytics, verification tags.', '<!-- e.g. a Google tag -->\n<script async src="https://www.googletagmanager.com/gtag/js?id=G-XXXX"></script>'],
              ['body', 'customBodyScripts', 'Added before </body> — chat widgets, support tools.', '<!-- e.g. a chat widget -->\n<script>/* … */</script>'],
            ].map(([value, key, hint, placeholder]) => (
              <TabsContent key={value} value={value}>
                <FormField label={value === 'css' ? 'Custom CSS' : `${value === 'head' ? 'Head' : 'Body'} scripts`} hint={hint}>
                  <Textarea
                    rows={12}
                    spellCheck={false}
                    className="tw-font-mono tw-text-[0.8125rem]"
                    placeholder={placeholder}
                    value={customCode[key]}
                    onChange={(e) => setCode(key, e.target.value)}
                  />
                </FormField>
              </TabsContent>
            ))}
          </Tabs>
        )}
      </AppCardContent>
    </AppCard>
  );
}

export default function DesignTab({
  layoutPreset, settingsBusy, onChangeLayout, brandingMode, setBrandingMode,
  customization, setCustomizationField, imageSlot, errors,
}) {
  const classic = layoutPreset === 'default';
  const theme = customization.theme;
  const setTheme = (key, value) => setCustomizationField('theme', key, value);
  const gradientSet = GRADIENT_KEYS.every((k) => theme[k].trim());
  const gradient = gradientSet ? GRADIENT_KEYS.map((k) => theme[k].trim()) : DEFAULT_HERO_GRADIENT;
  // The font preset reaches Classic always, and the other layouts only when
  // they are told to use the institute's brand.
  const fontApplies = classic || brandingMode === 'institute';

  return (
    <div className="tw-flex tw-flex-col tw-gap-5">
      <LayoutCard layoutPreset={layoutPreset} busy={settingsBusy} onChangeLayout={onChangeLayout} />

      <AppCard>
        <AppCardHeader>
          <AppCardTitle><Palette size={16} /> Colours and font</AppCardTitle>
          <AppCardDescription>Your brand colour and logo are set in Settings → General.</AppCardDescription>
        </AppCardHeader>
        <AppCardContent className="tw-flex tw-flex-col tw-gap-5">
          {!classic && (
            <fieldset className="tw-m-0 tw-flex tw-flex-col tw-gap-2.5 tw-border-0 tw-p-0">
              <legend className="tw-mb-2 tw-p-0 tw-text-sm tw-font-medium tw-text-content">Colours and fonts on this layout</legend>
              {BRANDING_MODES.map((m) => (
                <Radio
                  key={m.id}
                  name="cms-branding-mode"
                  label={m.label}
                  description={m.description}
                  checked={brandingMode === m.id}
                  onChange={() => setBrandingMode(m.id)}
                />
              ))}
            </fieldset>
          )}
          {fontApplies && (
            <div className="tw-grid tw-grid-cols-1 tw-gap-4 min-[721px]:tw-grid-cols-2">
              <FormField label={<><Type size={14} className="tw-mr-1 tw-inline" aria-hidden="true" />Font</>}>
                <NativeSelect value={theme.siteFontPreset} onChange={(e) => setTheme('siteFontPreset', e.target.value)}>
                  {FONT_PRESET_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </NativeSelect>
              </FormField>
              {theme.siteFontPreset === 'Custom' ? (
                <FormField
                  label="Font family (CSS)"
                  error={errors['theme.siteFontCustom']}
                  hint="Load the font with a stylesheet under Advanced: custom code if it is not a system font."
                >
                  <Input
                    spellCheck={false}
                    placeholder="'Playfair Display', serif"
                    value={theme.siteFontCustom}
                    onChange={(e) => setTheme('siteFontCustom', e.target.value)}
                  />
                </FormField>
              ) : (
                <HelpText className="tw-self-end tw-pb-2">Loaded from Google Fonts where needed.</HelpText>
              )}
            </div>
          )}
        </AppCardContent>
      </AppCard>

      {classic && (
        <AppCard>
          <AppCardHeader>
            <AppCardTitle><Sparkles size={16} /> Home page hero</AppCardTitle>
            <AppCardDescription>The dark band at the top of the home page, behind the headline.</AppCardDescription>
          </AppCardHeader>
          <AppCardContent className="tw-flex tw-flex-col tw-gap-5">
            <ImageField
              label="Background photo"
              hint="Optional. A dark overlay keeps the headline readable. While a photo is set, the gradient below is not used."
              error={errors['theme.heroBackgroundImageExternalUrl']}
              {...imageSlot('theme', 'heroBackgroundImage', 'hero-background')}
            />

            <div className="tw-flex tw-flex-col tw-gap-3">
              <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
                <span className="tw-text-sm tw-font-medium tw-text-content">Background gradient</span>
                <AppButton
                  size="sm"
                  variant="ghost"
                  onClick={() => GRADIENT_KEYS.forEach((k) => setTheme(k, ''))}
                  disabled={!GRADIENT_KEYS.some((k) => theme[k].trim())}
                >
                  <RotateCcw size={16} /> Use the default
                </AppButton>
              </div>
              <div
                aria-hidden="true"
                className="tw-h-16 tw-rounded-md tw-border tw-border-solid tw-border-line"
                style={{ background: `linear-gradient(135deg, ${gradient[0]} 0%, ${gradient[1]} 40%, ${gradient[2]} 70%, ${gradient[3]} 100%)` }}
              />
              <div className="tw-grid tw-grid-cols-1 tw-gap-3 min-[480px]:tw-grid-cols-2 min-[992px]:tw-grid-cols-4">
                {GRADIENT_KEYS.map((k, i) => (
                  <FormField key={k} label={GRADIENT_LABELS[i]}>
                    <ColorInput value={theme[k]} fallback={DEFAULT_HERO_GRADIENT[i]} onChange={(hex) => setTheme(k, hex)} />
                  </FormField>
                ))}
              </div>
              {errors['theme.heroGradient']
                ? <FieldError>{errors['theme.heroGradient']}</FieldError>
                : <HelpText>Set all four colours, or none for the default.</HelpText>}
            </div>

            <FormField label="Headline size" hint="Shrinks the big home-page headline on every screen size.">
              <NativeSelect value={String(theme.heroTitleScale)} onChange={(e) => setTheme('heroTitleScale', Number(e.target.value))}>
                {HERO_TITLE_SCALES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </NativeSelect>
            </FormField>
          </AppCardContent>
        </AppCard>
      )}

      {classic && (
        <AppCard>
          <AppCardHeader>
            <AppCardTitle>Course pages</AppCardTitle>
            <AppCardDescription>Promises printed under the price on every course page. Show only the ones you offer.</AppCardDescription>
          </AppCardHeader>
          <AppCardContent className="tw-flex tw-flex-col tw-gap-2.5">
            <Checkbox
              label="“30-day money-back guarantee”"
              checked={!theme.hideCourseMoneyBack}
              onChange={(e) => setTheme('hideCourseMoneyBack', !e.target.checked)}
            />
            <Checkbox
              label="“Lifetime access to course materials”"
              checked={!theme.hideCourseLifetimeAccess}
              onChange={(e) => setTheme('hideCourseLifetimeAccess', !e.target.checked)}
            />
          </AppCardContent>
        </AppCard>
      )}

      <CustomCodeCard
        customCode={customization.customCode}
        setCode={(key, value) => setCustomizationField('customCode', key, value)}
      />
    </div>
  );
}
